import { ENV_VARS } from "@/lib/env";
import { useCachedRequest } from "@/shared/hooks/use-cached-request";
import { profileURL } from "@/shared/hooks/use-profile";
import {
  type OrgUser,
  type OrgUserRole,
  OrgUserSchema,
  type UserInvite,
  UserInviteSchema,
} from "@/shared/models";
import { useUser } from "@/shared/providers/user-provider";
import { mutate } from "swr";
import { z } from "zod";

const organizationsPath = `${ENV_VARS.BASE_API_URL}/platform/v1/organizations`;
export const orgUrl = (orgId: string) => `${organizationsPath}/${orgId}`;

export function useOrg(orgId: string | undefined) {
  const { buildRequestHeaders } = useUser();

  const modifyOrg = async ({ name }: { name: string }) => {
    if (!orgId) {
      throw new Error("orgId is undefined");
    }

    const r = await fetch(orgUrl(orgId), {
      method: "POST",
      headers: await buildRequestHeaders(),
      body: JSON.stringify({ name }),
    });
    if (r.status !== 200) {
      const text = await r.text();
      const json = JSON.parse(text);
      if (json?.error?.message) {
        throw new Error(json.error.message);
      }
      throw new Error("Failed to modify Organization");
    }

    await mutate(`${orgUrl(orgId)}/users`);
    await mutate(profileURL);

    return r;
  };

  const modifyUserOrgRole = async (userId: string, role: OrgUserRole) => {
    if (!orgId) {
      throw new Error("orgId is undefined");
    }

    const r = await fetch(`${orgUrl(orgId)}/users/${userId}`, {
      method: "POST",
      headers: await buildRequestHeaders(),
      body: JSON.stringify({ role }),
    });

    mutate(`${orgUrl(orgId)}/users`);

    return r;
  };

  const inviteUsers = async (emails: string[], role: string) => {
    if (!orgId) {
      throw new Error("orgId is undefined");
    }

    const r = await fetch(`${orgUrl(orgId)}/invites`, {
      method: "POST",
      headers: await buildRequestHeaders(),
      body: JSON.stringify({ emails, role }),
    });

    mutate(`${orgUrl(orgId)}/users`);

    return r;
  };

  const deleteInvite = async (inviteId: string) => {
    if (!orgId) {
      throw new Error("orgId is undefined");
    }

    const r = await fetch(`${orgUrl(orgId)}/invites/${inviteId}`, {
      method: "DELETE",
      headers: await buildRequestHeaders(),
    });

    mutate(`${orgUrl(orgId)}/users`);

    if (!r.ok) {
      throw new Error("Failed to delete invite");
    }

    return r;
  };

  const removeUserFromOrg = async (userId: string) => {
    if (!orgId) {
      throw new Error("orgId is undefined");
    }

    const r = await fetch(`${orgUrl(orgId)}/users/${userId}`, {
      method: "DELETE",
      headers: await buildRequestHeaders(),
    });

    mutate(`${orgUrl(orgId)}/users`);

    if (!r.ok) {
      const text = await r.text();
      const json = JSON.parse(text);
      if (json?.error?.message) {
        throw new Error(json.error.message);
      }
      throw new Error("Failed to remove user from organization");
    }

    return r;
  };

  const approveTOS = async () => {
    if (!orgId) {
      throw new Error("orgId is undefined");
    }

    const r = await fetch(`${orgUrl(orgId)}/approve_tos`, {
      method: "POST",
      headers: await buildRequestHeaders(),
    });

    if (!r.ok) {
      throw new Error("Failed to approve TOS");
    }

    return r;
  };

  return {
    modifyOrg,
    inviteUsers,
    deleteInvite,
    removeUserFromOrg,
    modifyUserOrgRole,
    approveTOS,
  };
}

export function useOrgUsers(orgId: string | undefined): {
  canInvite: boolean | undefined;
  orgUsers: OrgUser[] | undefined;
  orgUserInvites: UserInvite[] | undefined;
  orgUsersLoading: boolean;
  orgUsersError: Error | undefined;
} {
  const schema = z.object({
    can_invite: z.boolean().optional(),
    members: z.object({
      data: z.array(OrgUserSchema),
    }),
    invited: z.array(UserInviteSchema).optional(),
  });

  const orgUsers = useCachedRequest<z.infer<typeof schema>>({
    url: orgId ? `${orgUrl(orgId)}/users` : null,
  });

  return {
    canInvite: orgUsers.data?.can_invite,
    orgUsers: orgUsers.data?.members.data,
    orgUserInvites: orgUsers.data?.invited,
    orgUsersLoading: orgUsers.isLoading,
    orgUsersError: orgUsers.error,
  };
}

export const OrgLogsSchema = z.object({
  data: z.array(
    z.object({
      request_id: z.string(),
      created_at: z.number(),
      status_code: z.number().int(),
      time_to_first_token: z.number(),
      time_to_completion: z.number(),
      model: z.string(),
      api_key_id: z.string().optional(),
      user_id: z.string().optional(),
      input_tokens: z.number().int(),
      input_audio_seconds: z.number().int(),
      output_tokens: z.number().int(),
      error: z.optional(
        z.object({
          message: z.string(),
          type: z.string(),
          param: z.optional(z.string()),
          code: z.optional(z.string()),
        }),
      ),
    }),
  ),
  paging: z.object({
    next_cursor: z.string().optional(),
  }),
});

export type OrgLogs = z.infer<typeof OrgLogsSchema>; // string
export type OrgLogsRow = OrgLogs["data"][number];
export type OrgLogsRowError = OrgLogsRow["error"];

export function useOrgLogs(
  orgId: string | undefined,
  minStatusCode: number,
  cursor: string,
  count: number,
): ReturnType<typeof useCachedRequest<z.infer<typeof OrgLogsSchema>>> {
  let url: string | null = null;

  if (orgId && cursor) {
    const urlBuilder = new URL(`${orgUrl(orgId)}/logs`);
    urlBuilder.searchParams.set("min_status_code", minStatusCode.toString());
    urlBuilder.searchParams.set("cursor", cursor);
    urlBuilder.searchParams.set("count", count.toString());
    url = urlBuilder.toString();
  }

  return useCachedRequest<z.infer<typeof OrgLogsSchema>>({
    url: url,
    parser: OrgLogsSchema.parse,
  });
}
